import { Link } from 'react-router-dom';
import { Text, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import type { ClientReview, MonthReadinessResponse } from '../../api/reviews';
import { formatCalendarDate } from '../../utils/calendarDate';
import { formatInstant } from '../../i18n/dates';
import { useSemanticColors, type SemanticColorKey } from '../../theme/semanticColors';
import i18n, { useTranslation } from '../../i18n';

/**
 * Twelve boxes for the client's year on the review page (reviewer proposal #2): which months are
 * reviewed, in review, waiting, or not covered by any review. The server derives it with the SAME
 * rule the Productivity bars use (`ClientMonthStatus`), so the strip and the chart cannot colour a
 * month differently, and the four colours and words are the Productivity ones.
 */

const STATES: Record<string, { labelKey: string; color: SemanticColorKey }> = {
  reviewed: { labelKey: 'monthState.reviewed', color: 'success' },
  'in-review': { labelKey: 'monthState.in-review', color: 'brand' },
  'not-started': { labelKey: 'monthState.not-started', color: 'warning' },
  none: { labelKey: 'monthState.none', color: 'neutral' },
};

/** "September" / "septiembre" in the UI language, from Intl on the UTC calendar. */
function monthName(index: number): string {
  return new Intl.DateTimeFormat(i18n.language, { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, index, 1)));
}

const useStyles = makeStyles({
  row: { display: 'flex', gap: tokens.spacingHorizontalXXS, flexWrap: 'wrap', listStyleType: 'none', margin: 0, padding: 0 },
  box: {
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textDecorationLine: 'none',
    boxSizing: 'border-box',
  },
  mine: {
    outline: `${tokens.strokeWidthThick} solid ${tokens.colorNeutralForeground1}`,
    outlineOffset: '1px',
  },
  // Not reviewable yet: faded, with a hatch, so it reads as "not yet" even without colour vision.
  notReady: {
    opacity: 0.45,
    backgroundImage: `repeating-linear-gradient(45deg, transparent 0 4px, ${tokens.colorNeutralStroke1} 4px 5px)`,
  },
  legend: {
    display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap', listStyleType: 'none',
    margin: `${tokens.spacingVerticalXS} 0 0 0`, padding: 0,
    color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200,
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
  swatch: { width: '12px', height: '12px', borderRadius: tokens.borderRadiusSmall, boxSizing: 'border-box' },
  wrap: { marginBottom: tokens.spacingVerticalL },
  caption: { color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalXS },
});

export function MonthStrip({ review, readiness }: { review: ClientReview; readiness?: MonthReadinessResponse }) {
  const styles = useStyles();
  const colors = useSemanticColors();
  const { t } = useTranslation();
  const strip = review.monthStrip;
  if (!strip || strip.months.length === 0) return null;
  // Readiness is only claimed when the backend could actually say, and only for the strip's own year.
  const ready = readiness?.available && readiness.year === strip.year
    ? new Map(readiness.months.map((r) => [r.month, r]))
    : null;

  return (
    <div className={styles.wrap}>
      <Text block size={200} className={styles.caption}>
        {t('monthStrip.caption', { year: strip.year })}
      </Text>
      <ul className={styles.row} aria-label={t('monthStrip.list', { year: strip.year, client: review.clientName })}>
        {strip.months.map((m) => {
          const state = STATES[m.state] ?? STATES.none;
          const palette = colors[state.color];
          const monthIndex = Number(m.month.slice(5, 7)) - 1;
          const name = `${monthName(monthIndex)} ${strip.year}`;
          const r = ready?.get(m.month);
          const notReady = r !== undefined && r.state !== 'ready';
          const label =
            t('monthStrip.box', { month: name, state: t(state.labelKey).toLowerCase() }) +
            // Who reviewed it and when (spec §10.13.1): the review credited with the month, the earliest completion.
            (m.state === 'reviewed' && m.reviewedByName
              ? t('monthStrip.reviewedBy', {
                  who: m.reviewedByName,
                  date: m.reviewedOn ? formatInstant(m.reviewedOn, 'dayYear') : '—',
                })
              : '') +
            (m.inThisReview ? t('monthStrip.inThisReview') : '') +
            (notReady
              ? ` · ${t(`monthReadiness.${r.state}`)}` +
                (r.taskSubject ? ` (${t('monthReadiness.task', { subject: r.taskSubject, due: formatCalendarDate(r.due, { withYear: true, fallback: '—' }) })})` : '')
              : '');
          // Another review holding the month links to it — including a month of THIS review that a
          // different review already covers (the coverage conflict, one click from the box). This
          // review's own months, held only by it, link nowhere.
          const other = m.reviewIds.find((id) => id !== review.id);
          const className = `${styles.box}${m.inThisReview ? ` ${styles.mine}` : ''}${notReady ? ` ${styles.notReady}` : ''}`;
          const style = {
            backgroundColor: m.state === 'none' ? tokens.colorNeutralBackground3 : palette.solid,
            color: m.state === 'none' ? tokens.colorNeutralForeground3 : palette.onFill,
          };
          const letter = monthName(monthIndex).slice(0, 1).toUpperCase();
          return (
            <li key={m.month} data-state={m.state} data-mine={m.inThisReview || undefined} data-readiness={notReady ? r.state : undefined}>
              <Tooltip content={label} relationship="description">
                {other ? (
                  <Link to={`/reviews/${other}`} className={className} style={style} aria-label={t('monthStrip.open', { label })}>
                    {letter}
                  </Link>
                ) : (
                  <span className={className} style={style} aria-label={label} role="img">
                    {letter}
                  </span>
                )}
              </Tooltip>
            </li>
          );
        })}
      </ul>
      {ready && (
        <ul className={styles.legend} aria-label={t('monthReadiness.legend.name')}>
          <li className={styles.legendItem}><span className={styles.swatch} style={{ backgroundColor: colors.success.solid }} aria-hidden />{t('monthReadiness.legend.reviewed')}</li>
          <li className={styles.legendItem}><span className={`${styles.swatch} ${styles.mine}`} aria-hidden />{t('monthReadiness.legend.thisReview')}</li>
          <li className={styles.legendItem}><span className={styles.swatch} style={{ backgroundColor: colors.warning.solid }} aria-hidden />{t('monthReadiness.legend.ready')}</li>
          <li className={styles.legendItem}><span className={`${styles.swatch} ${styles.notReady}`} style={{ backgroundColor: colors.warning.solid }} aria-hidden />{t('monthReadiness.legend.open')}</li>
          <li className={styles.legendItem}><span className={`${styles.swatch} ${styles.notReady}`} style={{ backgroundColor: tokens.colorNeutralBackground3 }} aria-hidden />{t('monthReadiness.legend.notEnded')}</li>
          <li className={styles.legendItem}><span className={`${styles.swatch} ${styles.notReady}`} style={{ backgroundColor: tokens.colorNeutralBackground3 }} aria-hidden />{t('monthReadiness.legend.noTask')}</li>
        </ul>
      )}
    </div>
  );
}
